import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { cn } from "@/lib/utils"

import { type QuestionAnswerTone } from "./questionAnswerState"

interface QuestionAnswerCardProps {
  id?: string
  className?: string
  tone?: QuestionAnswerTone
  isActive?: boolean
  header: React.ReactNode
  headerAction?: React.ReactNode
  body: React.ReactNode
  statusMessage?: React.ReactNode
  statusAction?: React.ReactNode
  footer?: React.ReactNode
}

const toneClassNames: Record<QuestionAnswerTone, string> = {
  neutral:
    "bg-grey2 border border-white/6 border-l-[3px] border-l-white/20",
  answered:
    "bg-grey2 border border-white/6 border-l-[3px] border-l-success/60",
  "low-confidence":
    "bg-grey2 border border-white/6 border-l-[3px] border-l-[#FFAB6F]/60",
  "needs-answer":
    "bg-grey2 border border-white/6 border-l-[3px] border-l-red1/60",
  unanswered:
    "bg-grey2 border border-white/6 border-l-[3px] border-l-red1/60",
}

const toneActiveBorderClassNames: Record<QuestionAnswerTone, string> = {
  neutral: "border-white/30!",
  answered: "border-success/60!",
  "low-confidence": "border-[#FFAB6F]/60!",
  "needs-answer": "border-red1/60!",
  unanswered: "border-red1/60!",
}

export const QuestionAnswerCard: React.FC<QuestionAnswerCardProps> = ({
  id,
  className,
  tone = "neutral",
  isActive = false,
  header,
  headerAction,
  body,
  statusMessage,
  statusAction,
  footer,
}) => {
  return (
    <Card
      id={id}
      className={cn(
        "relative overflow-hidden rounded-[14px] transition-colors ease-linear",
        toneClassNames[tone],
        isActive && toneActiveBorderClassNames[tone],
        className,
      )}
    >
      {isActive && (
        <div
          className="pointer-events-none absolute left-1/2 top-0 h-[180px] w-[120%] -translate-x-1/2 -translate-y-1/2 blur-md"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(255,244,64,0.12) 0%, rgba(255,244,64,0.03) 35%, transparent 70%)",
          }}
        />
      )}
      <CardHeader className="px-4 py-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">{header}</div>
          {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-2.5 p-4 pt-0">
        <div className="overflow-hidden rounded-[10px] bg-black/18">
          {body}
        </div>

        {(statusMessage || statusAction) && (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">{statusMessage}</div>
            {statusAction ? (
              <div className="shrink-0">{statusAction}</div>
            ) : null}
          </div>
        )}

        {footer ?? null}
      </CardContent>
    </Card>
  )
}
